import { useQuery, UseQueryResult } from '@tanstack/react-query'
import { getBlocks } from '@/ports'

type BlockType =
  | 'managed_users'
  | 'credit_checks'
  | 'custom_addenda'
  | 'custom_contracts'
  | 'automated_rent_collections'

type Error = {
  message: string
}

type Usage = {
  current_value: number
}

type Block = {
  name: BlockType
  overlimit_policy: string
  usage: Usage
  value: number
}

type Blocks = Record<BlockType, Block> | never[]

type Result = {
  base_plan_name: string
  billing_user_id: number
  blocks: Blocks
}

// Flatten BE response
type UserLimits = Omit<Result, 'blocks'> &
  Omit<Block, 'usage'> &
  Usage & {
    allowed: boolean
  }

// This data is a placeholder for users
// who might be missing the data patch.
const getPlaceholder = (block: BlockType): Block => ({
  name: block,
  value: 20,
  overlimit_policy: 'deny',
  usage: { current_value: 0 }
})

const getData = async (block: BlockType): Promise<Result> => {
  try {
    const result = await getBlocks({ block })
    if (!result) throw new Error()
    const data: Result = result.body
    return data
  } catch (err) {
    throw err
  }
}

const select = (block: BlockType, data: Result): UserLimits => {
  const { blocks, base_plan_name, billing_user_id } = data
  const main = Array.isArray(blocks) ? getPlaceholder(block) : blocks[block]
  const { name, value, overlimit_policy, usage } = main
  const { current_value } = usage || { current_value: -1 }
  return {
    base_plan_name,
    billing_user_id,
    name,
    overlimit_policy,
    current_value,
    value,
    allowed: current_value < value
  }
}

type QueryResult = UseQueryResult<UserLimits, Error>

type Return = {
  data: QueryResult['data'] | undefined
  error: QueryResult['error']
  isLoading: boolean
  isError: boolean
}

type Options = {
  enabled?: boolean
}

const useBlocks = (block: BlockType, options: Options = { enabled: true }): Return => {
  const { data, isLoading, error, isFetching, isError } = useQuery<Result, Error, UserLimits>({
    queryKey: ['companyManagement', 'blocks', block],
    queryFn: () => getData(block),
    ...options,
    staleTime: 5 * 60000,
    retry: false,
    select: (...args) => select(block, ...args)
  })
  // We want to clear the old data even when the new data is fetching
  const loading = isLoading || isFetching
  return { data: loading ? undefined : data, error, isLoading: loading, isError }
}

export const useUserLimits = (options?: Options) => useBlocks('managed_users', options)
// export const useCreditLimits = (options?: Options) => useBlocks('credit_checks', options)
export const useAddendaLimits = (options?: Options) => useBlocks('custom_addenda', options)
export const useContractLimits = (options?: Options) => useBlocks('custom_contracts', options)
export const useAutomatedRentLimits = (options?: Options) =>
  useBlocks('automated_rent_collections', options)
